import VideoMp4 from "../../../static/media/case-studies/Tripadvisor_CaseStudy_Title-Video-720.mp4";
import VideoWebm from "../../../static/media/case-studies/Tripadvisor_CaseStudy_Title-Video-720.webm";
import Poster from "../../../static/media/case-studies/Tripadvisor_CaseStudy_Title-Video.webp";
import { filterListByCurrentIndustryCode } from "../../Utils";
import { Footer } from "../../components/Footer";
import { Layout } from "../../components/Layout";
import { Video } from "../../components/Organisms/Video/Video";
import { RelatedCaseStudies } from "../../components/RelatedCaseStudies";
import { useIsMobile } from "../../hooks/useIsMobile";
import { graphql } from "gatsby";
import * as React from "react";
import { Helmet } from "react-helmet";

export default function CaseStudy({ data, location }) {
  const isMobile = useIsMobile(620);
  const [reducedMotion, setReducedMotion] = React.useState(false);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setReducedMotion(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return (
    <Layout location={location}>
      <main id="main" className="marketing-content container-fluid">
        <Helmet
          bodyAttributes={{
            class: "body-shorthand body-marketing body-marketing-secondary body-marketing-case-studies hide-elevio",
          }}
        >
          <title>Tripadvisor | Shorthand Case Study</title>
          <meta name="description" content="Here's how Tripadvisor took Shorthand from a content tool to a certified pitch winner." />
        </Helmet>
        <div className="row section-title">
          <div className="col-sm-12 col-md-10 col-lg-8 col-xl-6  mx-auto  section-inner">
            <h1>
              <span className="shorthand-logo">Shorthand</span>
              <span className="subsection-label">Case Study:</span>
              Tripadvisor
            </h1>
            <h2>Here&apos;s how Tripadvisor took Shorthand from a content tool to a certified pitch winner.</h2>
          </div>
          <div aria-hidden="true" className="background-media">
            <div className="tw-absolute tw-inset-0 tw-z-10 tw-bg-black tw-bg-opacity-65"></div>
            {isMobile || reducedMotion ? (
              <img
                src="/media/case-studies/Tripadvisor_CaseStudy_Title-IMG.png"
                data-object-fit="cover"
                data-autoscale-content-ratio="1.77777777778"
                alt=""
              />
            ) : (
              <Video poster={Poster} videoMp4={VideoMp4} videoWebm={VideoWebm} />
            )}
          </div>
        </div>
        <div className="row section">
          <div className="col-sm-12 col-md-10 col-lg-8 col-xl-6  mx-auto marketing-main-column">
            <h3 className="intro">
              Starting on the platform in 2018, Tripadvisor uses Shorthand to run rich storytelling campaigns on its own site, and to
              build partner pieces and pitches in its Wanderlab creative agency.
            </h3>
            <br />
            <div className="inline-image featured ">
              <img src="/media/case-studies/Tripadvisor_CaseStudy_Title-IMG.gif" alt="Tripadvisor" data-object-fit="cover" />
            </div>
            <p>
              Prior to beginning with Shorthand, the team had been custom coding all of its content pages, using producers, rather than
              designers, to build. Starting with Shorthand lifted work off the producers, empowering the designers to build stunning
              stories themselves with minimal coding knowledge.{" "}
            </p>
            <figure className="block quote-block offset left">
              <blockquote>
                <p>“It&apos;s nice to use for the pitch phase so clients can actually see how the page will flow.”</p>
              </blockquote>
              <figcaption className="quote-cite">Sara Jeong</figcaption>
            </figure>
            <p>
              Shorthand was the perfect solution; “It saved us time and gave us more custom functionality,” says Sara Jeong, Creative
              Manager, Design, at Tripadvisor&apos;s Wanderlab agency.{" "}
            </p>
            <p>
              Today, “It&apos;s in almost every campaign that we do,” from material for Tripadvisor&apos;s global audience, to creative
              pitches for client brands.
            </p>
            <div className="photo-grid flex featured">
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Tripadvisor_CaseStudy_TransparencyReport_1.png"
                alt="tripadvisor repor"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Tripadvisor_CaseStudy_TransparencyReport_2.png"
                alt="tripadvisor report"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Tripadvisor_CaseStudy_TransparencyReport_3.png"
                alt="tripadvisor report"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Tripadvisor_CaseStudy_TransparencyReport_4.png"
                alt="tripadvisor report"
              />
            </div>
            <p>
              The{" "}
              <a href="https://www.tripadvisor.com/TransparencyReport2023" target="_blank">
                2023 Review Transparency Report
              </a>{" "}
              is one story that makes the Tripadvisor team particularly proud.
            </p>
            <p>
              It has racked up 813,000 views in English alone, and is published in six languages on Shorthand. Its full-width video and
              striking photography, combined with its simple, on-brand data visualisations, make for a memorable and enjoyable read.
              It&apos;s testament to the Tripadvisor team&apos;s design prowess, using Shorthand to create something entirely unique to
              its brand.
            </p>
            <p>Shorthand stories "Really make our research more digestible and engaging to read," says Jeong.</p>
            <h3 className="subheading">Wanderlab: bringing breathtaking sponsored content to travellers</h3>
            <p>
              Wanderlab, Tripadvisor&apos;s creative content marketing agency arm that launched in 2022, helps client brands connect
              with travel-curious adventurers the world over. Wanderlab has partnered with Corona, Doordash, Disney, Ford, Hilton, and
              "dozens, and dozens, and dozens more", says Anna Aksman, Director of Global Creative Operations at Tripadvisor.
            </p>
            <div className="photo-grid flex featured">
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Tripadvisor_CaseStudy_ThisIsLivingChile_1.png"
                alt="Tripadvisor casetudy"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Tripadvisor_CaseStudy_ThisIsLivingChile_2.png"
                alt="Tripadvisor casetudy"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Tripadvisor_CaseStudy_ThisIsLivingChile_3.png"
                alt="Tripadvisor casetudy"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Tripadvisor_CaseStudy_ThisIsLivingChile_4.png"
                alt="Tripadvisor casetudy"
              />
            </div>
            <p>
              One great example is the 'This is Living&apos; campaign, made with Corona. Dedicated content hubs showcase the beauty and
              appeal of a selection of locations around the globe, including{" "}
              <a href="https://www.tripadvisor.com/InfoCenter-a_ctr.corona_thisisliving_chile_en" target="_blank">
                Chile
              </a>
              , South Africa, and Peru. "We really pushed the boundary with Shorthand on that one", says Jeong.
            </p>
            <figure className="block quote-block offset right">
              <blockquote>
                <p>
                  "Anecdotally, the more beautiful and engaging and interactive our pitch phase is, the shorter the 'close&apos; window
                  is, and the more inclined people are to say yes"
                </p>
              </blockquote>
              <figcaption className="quote-cite">Anna Aksman</figcaption>
            </figure>
            <p>
              The pages combine video, deep linking, 'mini menus', recommendations, ratings, and enticing photography, and end with a
              call-to-action to Corona&apos;s Sunsets series of global events. "It&apos;s quite a lot of content, organised in an
              efficient way," Jeong says.
            </p>
            <p>
              We&apos;re sure you&apos;ll agree. 'This is Living&apos; is a great demonstration of how, using Shorthand, publishers can
              stay true to their brand while building exciting new media partnerships or sponsored content products and stories.
            </p>
            <p>
              {" "}
              Hosted on Tripadvisor&apos;s pages, under its own branding, This is Living still manages to inject the Corona brand in a
              way that makes total sense to the reader.
            </p>
            <div className="photo-grid flex featured">
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Tripadvisor_CaseStudy_BlackTravelToday_1.png"
                alt="Tripadvisor casetudy"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Tripadvisor_CaseStudy_BlackTravelToday_2.png"
                alt="Tripadvisor casetudy"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Tripadvisor_CaseStudy_BlackTravelToday_3.png"
                alt="Tripadvisor casetudy"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Tripadvisor_CaseStudy_BlackTravelToday_4.png"
                alt="Tripadvisor casetudy"
              />
            </div>
            <p>
              The Tripadvisor team&apos;s ability to blend its brand style with that of its partners, all the while crafting
              captivating storytelling and presenting data in accessible ways, also shines through in its work with Group Black.
            </p>
            <p>
              {" "}
              The '
              <a href="https://www.tripadvisor.com/InfoCenter-a_ctr.groupblack?mcid=66484" target="_blank">
                Black Travel Today
              </a>
              &apos; report surveyed Black travellers and presented the findings: how Black travellers determine safe destinations, how
              they seek cultural connections, and the types of destinations they prefer.
            </p>
            <figure className="block quote-block offset left">
              <blockquote>
                <p>
                  "We work with digital marketers, and for them to be able to take this and show off our work up the ladder has been
                  really great."
                </p>
              </blockquote>
              <figcaption className="quote-cite">Anna Aksman</figcaption>
            </figure>
            <p>
              Aside from being a striking story and a valuable eye-opener about the experience of Black travellers in North America, it
              also hits the mark in its commercial goal.
            </p>{" "}
            <p>
              It outlines the business opportunity for travel brands to build "connections with Black travellers, through authentic
              representation and community engagement," says Tripadvisor&apos;s Adam Ochman, Global Director of Marketing Solutions.
            </p>
            <p>
              The creative excellence paired with the emotional appeal of an important issue made for a very successful campaign.
              "There was a lot of press about this one here in the US", Aksman says.
            </p>
            <h3 className="subheading">Partnering up</h3>
            <p>
              The traffic enjoyed by stories like the Transparency Report is only one part of the value for Tripadvisor and the
              Wanderlab team. Shorthand has also been a major boon in pitching work to clients. "Using this as a sales tool is really
              important", Aksman says.
            </p>
            <p>
              {" "}
              "Anecdotally, the more beautiful and engaging and interactive our pitch phase is, the shorter the 'close&apos; window is,
              and the more inclined people are to say yes," says Aksman. "So it&apos;s a critical tool for us, for building business."
            </p>
            <p>
              Showcasing the functionality not just to creative teams, but also to managers and a broader range of stakeholders is
              invaluable. "It&apos;s nice to use for the pitch phase so clients can actually see how the page will flow, and we can
              actually scroll through the preview link rather than having just a small static mock on the slide," says Jeong.
            </p>
            <figure className="block quote-block offset right">
              <blockquote>
                <p>"It&apos;s a critical tool, for us, for building business."</p>
              </blockquote>
              <figcaption className="quote-cite">Anna Aksman</figcaption>
            </figure>
            <p>
              The inherent value of beautifully-designed and memorable storytelling is not lost here. "Sometimes performance isn&apos;t
              even a thing in those conversations," says Aksman. "Clients are seeing how purposeful the co-branding is, and how
              immersive the pages are. We have a lot of excitement from brands internally at their companies."
            </p>
            <p>
              So what are Tripadvisor&apos;s tips for other teams doing premium visual storytelling? For Jeong it has to do with
              rolling up the sleeves and getting started. "It just takes being on the platform a lot, trying out different tools,
              looking at all the different sections.
            </p>
            <p>
              {" "}
              "The{" "}
              <a href="https://shorthand.com/featured/stories/" target="_blank">
                inspiration page
              </a>{" "}
              about how other brands are using Shorthand is really helpful."
            </p>
            <p> "Using Shorthand almost every day helps you explore all the different features."</p>
            <div>
              <h3 className="subheading">The road ahead</h3>
              <p>
                On introducing the Shorthand platform to new members of the team, Aksman has also noticed "it&apos;s not a particularly
                steep learning curve."
              </p>

              <p>
                "It feels quite 'turnkey&apos; as far as builders of this calibre go." Shorthand&apos;s speed to mobilisation is
                powerful. "Being able to move quickly and make work that's impressive in a short period of time is going to be really
                important," she says.
              </p>
            </div>
          </div>
        </div>
        <RelatedCaseStudies studies={filterListByCurrentIndustryCode(data, "tripadvisor")} />
        <Footer />
      </main>
    </Layout>
  );
}

export const query = graphql`
  query RelxQuery {
    allCaseStudiesJson {
      edges {
        node {
          jsonId
          image
          industry
          industryCode
          organisation
          summary
        }
      }
    }
  }
`;
